import { prepareSiteFiles } from './build/export.js';
import { MODEL } from './constants.js';
import { $, esc, hide, show, showToast } from './dom.js';
import { PROMPTS } from './prompts/index.js';
import { ST } from './state.js';

// ── API KEY ───────────────────────────────────────────────────────

/**
 * Ensure API key is available, showing modal if needed.
 * @param {() => void} fn - Callback to run once key is available
 */
function requireKey(fn) {
  if (ST.apiKey) {
    fn();
    return;
  }
  ST.pendingFn = fn;
  show('api-modal');
  setTimeout(() => $('api-inp')?.focus(), 80);
}

/** @returns {Promise<void>} */
function requireKeyAsync() {
  return new Promise(resolve => {
    if (ST.apiKey) {
      void resolve();
      return;
    }
    requireKey(() => resolve());
  });
}

function saveKey() {
  const k = $('api-inp')?.value?.trim() || '';
  const errEl = $('api-err');
  if (!k.startsWith('sk-ant-')) {
    if (errEl) {
      errEl.style.display = 'block';
      errEl.textContent = 'Invalid key. Keys start with sk-ant-';
    }
    return;
  }
  if (errEl) {
    errEl.style.display = 'none';
  }
  ST.apiKey = k;
  localStorage.setItem('sf_key', k);
  hide('api-modal');
  if (ST.pendingFn) {
    const fn = ST.pendingFn;
    ST.pendingFn = null;
    fn();
  }
}

// ── CLOUDFLARE DEPLOYMENT (candidate for backend migration) ─────
// During Vue migration, move CF deploy logic to backend API endpoint.

function showPublishModal() {
  const tokenInp = $('cf-token-inp');
  const projInp = $('cf-project-inp');
  const preview = $('cf-url-preview');
  if (tokenInp) {
    tokenInp.value = ST.cfToken;
  }
  if (projInp) {
    projInp.value = ST.cfProject;
  }
  if (preview) {
    preview.textContent = ST.cfProject || 'your-project';
  }
  const pubStatus = $('pub-status');
  const pubResult = $('pub-result');
  const pubErr = $('pub-err');
  const pubBtn = $('pub-btn');
  if (pubStatus) {
    pubStatus.style.display = 'none';
  }
  if (pubResult) {
    pubResult.style.display = 'none';
  }
  if (pubErr) {
    pubErr.style.display = 'none';
  }
  if (pubBtn) {
    pubBtn.disabled = false;
    pubBtn.textContent = 'Publish';
  }
  show('pub-modal');
  setTimeout(() => {
    (ST.cfToken ? $('cf-project-inp') : $('cf-token-inp'))?.focus();
  }, 80);
}

function saveCfCreds() {
  const token = $('cf-token-inp')?.value?.trim() || '';
  const raw = $('cf-project-inp')?.value?.trim() || '';
  const project = raw.toLowerCase().replace(/[^a-z0-9-]/g, '');
  const errEl = $('pub-err');
  if (!token) {
    if (errEl) {
      errEl.style.display = 'block';
      errEl.textContent = 'API token is required.';
    }
    return false;
  }
  if (!project || project.length < 2) {
    if (errEl) {
      errEl.style.display = 'block';
      errEl.textContent =
        'Project name must be at least 2 characters (lowercase letters, numbers, hyphens).';
    }
    return false;
  }
  if (errEl) {
    errEl.style.display = 'none';
  }
  // Clear cached account if token changed
  if (token !== ST.cfToken) {
    ST.cfAccountId = '';
    localStorage.removeItem('sf_cf_account');
  }
  ST.cfToken = token;
  ST.cfProject = project;
  localStorage.setItem('sf_cf_token', token);
  localStorage.setItem('sf_cf_project', project);
  return true;
}

async function cfApi(action, extra = {}) {
  const res = await fetch('/api/cf-deploy', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, cfToken: ST.cfToken, ...extra }),
  });
  return res.json();
}

async function resolveCfAccount() {
  if (ST.cfAccountId) {
    return ST.cfAccountId;
  }
  const data = await cfApi('get-accounts');
  if (!(data.success && data.result?.length)) {
    throw new Error(
      'Could not resolve account. Check your API token permissions (needs Account Settings:Read).',
    );
  }
  ST.cfAccountId = data.result[0].id;
  localStorage.setItem('sf_cf_account', ST.cfAccountId);
  return ST.cfAccountId;
}

function Uint8ToBase64(bytes) {
  let binary = '';
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

async function CfDeploy(accountId, files) {
  const payload = [];
  for (const file of files) {
    const bytes = file.binary ? file.content : new TextEncoder().encode(file.content);
    const b64 = Uint8ToBase64(bytes);
    const hashBuf = await crypto.subtle.digest('SHA-256', bytes);
    const hash = [...new Uint8Array(hashBuf)]
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
      .slice(0, 32);
    payload.push({ hash, path: file.path, b64 });
  }

  return cfApi('deploy', { accountId, projectName: ST.cfProject, files: payload });
}

async function publishToCloudflare() {
  const statusEl = $('pub-status');
  const resultEl = $('pub-result');
  const errEl = $('pub-err');
  const btn = $('pub-btn');

  function setStatus(msg) {
    if (statusEl) {
      statusEl.style.display = 'block';
      statusEl.textContent = msg;
    }
  }

  function setError(msg) {
    if (errEl) {
      errEl.style.display = 'block';
      errEl.textContent = msg;
    }
    if (statusEl) {
      statusEl.style.display = 'none';
    }
    if (btn) {
      btn.disabled = false;
    }
  }

  if (resultEl) {
    resultEl.style.display = 'none';
  }
  if (errEl) {
    errEl.style.display = 'none';
  }
  if (btn) {
    btn.disabled = true;
  }

  try {
    // Step 1: Resolve account
    setStatus('Resolving account…');
    const accountId = await resolveCfAccount();

    // Step 2: Prepare files (build/export.js)
    setStatus('Preparing files…');
    const baseUrl = `https://${ST.cfProject}.pages.dev`;
    const files = prepareSiteFiles({ baseUrl });

    if (!files.length) {
      setError('No pages to deploy. Build at least one page first.');
      return;
    }

    // Step 3: Load BLAKE3 + build FormData + deploy
    setStatus('Loading crypto…');
    let deployResult = await CfDeploy(accountId, files);

    // If project doesn't exist, create it first then retry
    const notFound =
      !deployResult.success &&
      deployResult.errors?.some(
        e => e.code === 8_000_007 || /not found|does not exist/i.test(e.message),
      );
    if (notFound) {
      setStatus('Creating project…');
      const createResult = await cfApi('create-project', { accountId, projectName: ST.cfProject });
      if (!createResult.success) {
        setError(createResult.errors?.[0]?.message || 'Failed to create project');
        return;
      }
      setStatus('Deploying to Cloudflare Pages…');
      deployResult = await CfDeploy(accountId, files);
    }

    if (!deployResult.success) {
      setError(deployResult.errors?.[0]?.message || 'Deployment failed');
      return;
    }

    // Step 4: Show success
    if (statusEl) {
      statusEl.style.display = 'none';
    }
    const url = `https://${ST.cfProject}.pages.dev`;
    if (resultEl) {
      resultEl.style.display = 'block';
      resultEl.innerHTML = `<div style="background:var(--s2);border-radius:var(--r);padding:12px;text-align:center">
        <div style="font-size:13px;color:var(--teal);font-weight:600;margin-bottom:6px">Deployed successfully!</div>
        <a href="${esc(url)}" target="_blank" style="font-size:14px;color:var(--acc);font-weight:600;word-break:break-all">${esc(url)}</a>
      </div>`;
    }
    if (btn) {
      btn.disabled = false;
      btn.textContent = 'Republish';
    }
    showToast('Site published to Cloudflare Pages!');
  } catch (e) {
    setError(e.message || 'Publish failed');
  }
}

// ── CLAUDE API ────────────────────────────────────────────────────

/**
 * Look up a skill/prompt config by ID.
 * @param {string} id
 * @returns {import('./types.ts').SkillConfig}
 */
function skill(id) {
  const found = PROMPTS[id];

  if (!found) {
    throw new Error(`Unknown skill: "${id}". Available: ${Object.keys(PROMPTS).join(', ')}`);
  }

  return found;
}

/**
 * Stream Claude API messages via SSE.
 * @param {object} payload - Request payload
 * @param {(html: string) => void} [onChunk] - Called with accumulated text on each chunk
 * @param {(ev: {active: boolean, text: string}) => void} [onThinking] - Thinking events
 * @returns {Promise<string>} Full response text
 */
async function streamMessages(payload, onChunk, onThinking) {
  const res = await fetch('/api/messages', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    if (res.status === 401) {
      ST.apiKey = '';
      localStorage.removeItem('sf_key');
      throw new Error('API key invalid — please re-enter');
    }
    throw new Error(err.error?.message || `Claude API error ${res.status}`);
  }

  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let full = '';
  let buf = '';
  let streamDone = false;
  let IsThinking = false;
  let ThinkingText = '';

  // rAF throttle state
  let rafId = null;
  let pendingHTML = null;
  let finished = false;

  function scheduleRender(html) {
    if (!onChunk) {
      return;
    } // No callback — just accumulate silently
    pendingHTML = html;
    if (rafId === null) {
      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (!finished && pendingHTML !== null) {
          onChunk(pendingHTML);
          pendingHTML = null;
        }
      });
    }
  }

  function flushRender() {
    if (!onChunk) {
      return;
    }
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    if (pendingHTML !== null) {
      onChunk(pendingHTML);
      pendingHTML = null;
    }
  }

  function processLine(ln) {
    if (!ln.startsWith('data: ')) {
      return;
    }
    const raw = ln.slice(6).trim();
    if (!raw || raw === '[DONE]') {
      return;
    }

    let ev;
    try {
      ev = JSON.parse(raw);
    } catch (e) {
      console.error('SSE JSON parse error, skipping line:', e, 'raw:', raw);
      return;
    }

    if (ev.type === 'content_block_delta' && ev.delta?.type === 'text_delta' && ev.delta?.text) {
      full += ev.delta.text;
      scheduleRender(full);
    } else if (
      ev.type === 'content_block_delta' &&
      ev.delta?.type === 'thinking_delta' &&
      ev.delta?.thinking
    ) {
      ThinkingText += ev.delta.thinking;
      if (onThinking) {
        onThinking({ active: true, text: ThinkingText });
      }
    } else if (ev.type === 'content_block_start' && ev.content_block?.type === 'thinking') {
      IsThinking = true;
      ThinkingText = '';
      if (onThinking) {
        onThinking({ active: true, text: '' });
      }
    } else if (ev.type === 'content_block_stop' && IsThinking) {
      IsThinking = false;
      if (onThinking) {
        onThinking({ active: false, text: ThinkingText });
      }
    } else if (ev.type === 'message_stop') {
      streamDone = true;
    } else if (ev.type === 'error') {
      const e = new Error(ev.error?.message || 'Stream error');
      e._streamError = true;
      throw e;
    }
  }

  function processBuffer() {
    const lines = buf.split('\n');
    buf = lines.pop() ?? '';
    for (const ln of lines) {
      processLine(ln);
      if (streamDone) {
        break;
      }
    }
  }

  try {
    while (!streamDone) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      buf += dec.decode(value, { stream: true });
      processBuffer();
    }

    // Flush remainder — last line may have no trailing \n
    if (buf.trim()) {
      processLine(buf.trim());
      buf = '';
    }

    flushRender();
  } catch (e) {
    flushRender();
    if (e._streamError) {
      throw e;
    }
    throw e;
  } finally {
    finished = true;
    if (!streamDone) {
      reader.cancel().catch(() => {
        /* intentionally ignored */
      });
    }
  }

  return full;
}

/**
 * Rasterize an SVG to PNG via canvas (Claude vision doesn't support SVG).
 * @param {string} src - data-URI or URL of the SVG
 * @returns {Promise<string>} base64-encoded PNG data (no prefix)
 */
const rasterizeSvg = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth || 512;
      const h = img.naturalHeight || 512;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/png').replace(/^data:[^;]+;base64,/, ''));
    };
    img.onerror = () => reject(new Error('Failed to rasterize SVG'));
    img.crossOrigin = 'anonymous';
    img.src = src;
  });

/**
 * High-level Claude API call with images, docs, streaming.
 * @param {import('./types.ts').ClaudeOptions} opts
 * @returns {Promise<string>} Full response text
 */
async function claude(opts) {
  const sys = opts.sys,
    user = opts.user,
    imgs = opts.imgs || [],
    docs = opts.docs || [],
    tokens = opts.tokens || 1500,
    onChunk = opts.onChunk,
    model = opts.model,
    temp = opts.temp,
    effort = opts.effort,
    thinking = opts.thinking,
    schema = opts.schema;
  if (!ST.apiKey) {
    throw new Error('No API key');
  }
  // Build user content: documents first (longform data at top), then images, then text (instructions at end)
  const hasBlocks = imgs.length || docs.length;
  const blocks = [];
  for (const d of docs) {
    blocks.push({
      type: 'document',
      source: { type: 'text', media_type: d.mediaType || 'text/plain', data: d.data },
      ...(d.title ? { title: d.title } : {}),
      ...(d.context ? { context: d.context } : {}),
    });
  }
  for (const i of imgs) {
    const isSvg =
      ('type' in i && i.type && i.type.includes('svg')) ||
      ('url' in i && /\.svg(\?|$)/i.test(i.url));
    // Rasterize SVG to PNG — Claude vision doesn't support SVG
    if (isSvg) {
      try {
        const src = 'url' in i ? i.url : `data:${i.type};base64,${i.data}`;
        const pngData = await rasterizeSvg(src);
        if (i.label) {
          blocks.push({ type: 'text', text: i.label });
        }
        blocks.push({
          type: 'image',
          source: { type: 'base64', media_type: 'image/png', data: pngData },
        });
      } catch {
        // CORS or load failure — skip silently
      }
      continue;
    }
    if (i.label) {
      blocks.push({ type: 'text', text: i.label });
    }
    if ('url' in i) {
      // URL-based — API fetches server-side, no client encoding needed
      blocks.push({ type: 'image', source: { type: 'url', url: i.url } });
    } else {
      // Base64-encoded (uploads, logos, screenshots)
      blocks.push({
        type: 'image',
        source: { type: 'base64', media_type: i.type || 'image/jpeg', data: i.data },
      });
    }
  }
  if (hasBlocks) {
    blocks.push({ type: 'text', text: user });
  }
  const userContent = hasBlocks ? blocks : user;
  const fullSys = sys;
  // Support explicit multi-turn messages array (bypasses single-turn user construction)
  const msgs = opts.messages || [{ role: 'user', content: userContent }];
  const payload = {
    _apiKey: ST.apiKey,
    model: model || MODEL,
    max_tokens: tokens,
    stream: true, // Always stream to avoid request timeout on long generations
    system: fullSys,
    messages: msgs,
  };
  if (typeof temp === 'number') {
    payload.temperature = temp;
  }
  if (effort) {
    payload.output_config = { ...(payload.output_config || {}), effort };
  }
  if (thinking) {
    payload.thinking = thinking;
  }
  if (schema) {
    payload.output_config = {
      ...(payload.output_config || {}),
      format: { type: 'json_schema', schema },
    };
  }

  const result = await streamMessages(payload, onChunk, opts.onThinking);

  console.groupCollapsed(
    `%c[claude] %c${model || MODEL}%c  ${tokens} tok  ${result.length} chars`,
    'color:#8b5cf6;font-weight:bold',
    'color:#06b6d4',
    'color:inherit',
  );
  console.log('system:', fullSys);
  console.log('user:', user);
  if (imgs.length) {
    console.log('images:', imgs.length);
  }
  if (docs.length) {
    console.log('docs:', docs.length);
  }
  console.log('response:', result);
  console.groupEnd();

  return result;
}

// ── MODULE EXPORTS ──────────────────────────────────────────────
export {
  CfDeploy,
  cfApi,
  claude,
  publishToCloudflare,
  requireKey,
  requireKeyAsync,
  resolveCfAccount,
  saveCfCreds,
  saveKey,
  showPublishModal,
  skill,
  streamMessages,
  Uint8ToBase64,
};
